import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { Link, useHistory } from "react-router-dom";
import { TbBuildingBank, TbCash, TbWallet, TbX } from "react-icons/tb";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { fetchBaasAccountsForEntity } from "../../../../../Services/baasAccountsListApi";
import { fetchBaasStablesWalletsForAccounts } from "../../../../../Services/baasStablesWalletApi";
import { fetchBaasVirtualAccounts } from "../../../../../Services/baasVirtualAccountsApi";
import { hasBaasBankingRails } from "../../baasCapabilities";
import {
  BAAS_ACCOUNTS_PATH,
  BAAS_STABLES_WALLET_PATH,
  BAAS_VIRTUAL_ACCOUNTS_PATH,
  buildBaasAccountDetailPath,
  buildBaasStablesWalletDetailPath,
} from "../../constants";
import { BaasAccountStatusBadge } from "../../Accounts/components/BaasAccountStatusBadge";
import { SectionHeading } from "./baasEntidadeCreateFields";

const SUMMARY_CARDS = [
  {
    key: "accounts",
    icon: TbBuildingBank,
    labelKey: "baas.entidades.detail.summary.accounts",
    listTitleKey: "baas.entidades.detail.accountsListTitle",
    emptyKey: "baas.entidades.detail.accountsEmpty",
    viewAllPath: BAAS_ACCOUNTS_PATH,
    viewAllKey: "baas.entidades.detail.viewAllAccounts",
    /** Contas correntistas — not issuer shells. */
    isVisible: (capabilities) => hasBaasBankingRails(capabilities),
  },
  {
    key: "virtualWallets",
    icon: TbWallet,
    labelKey: "baas.entidades.detail.summary.virtualWallets",
    listTitleKey: "baas.entidades.detail.virtualWalletsListTitle",
    emptyKey: "baas.entidades.detail.virtualWalletsEmpty",
    viewAllPath: BAAS_VIRTUAL_ACCOUNTS_PATH,
    viewAllKey: "baas.entidades.detail.viewAllVirtualAccounts",
    isVisible: (capabilities) => hasBaasBankingRails(capabilities),
  },
  {
    key: "stablesWallets",
    icon: TbCash,
    labelKey: "baas.entidades.detail.summary.stablesWallets",
    listTitleKey: "baas.entidades.detail.stablesWalletsListTitle",
    emptyKey: "baas.entidades.detail.stablesWalletsEmpty",
    viewAllPath: BAAS_STABLES_WALLET_PATH,
    viewAllKey: "baas.entidades.detail.viewAllStablesWallets",
    isVisible: (capabilities) => capabilities?.products?.stables?.enabled === true,
  },
];

const accountTypeLabel = (account) => {
  const type = String(account?.type || "").trim();
  if (!type) return "—";
  const key = `baas.accounts.type.${type}`;
  const label = intl.get(key);
  return label && label !== key ? label : type;
};

const AccountRow = ({ account, onNavigate }) => {
  const to = buildBaasAccountDetailPath(account.id);

  return (
    <button
      type="button"
      className="flex w-full min-w-0 items-center justify-between gap-3 rounded-lg px-2 py-3 text-left transition hover:bg-gray-50/80 dark:hover:bg-brand-dark-elevated/40"
      onClick={() => onNavigate(to)}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100">
          {account.alias || account.accountNumber || account.id}
        </p>
        <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">{account.id}</p>
        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{accountTypeLabel(account)}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <BaasAccountStatusBadge status={account.status} />
        <span className="text-xs font-semibold text-[#a66f1d] dark:text-brand-gold-mid">
          {intl.get("baas.entidades.detail.openAccount")} →
        </span>
      </div>
    </button>
  );
};

const WalletRow = ({ wallet, onNavigate }) => {
  const to = wallet.id
    ? buildBaasStablesWalletDetailPath(wallet.id)
    : wallet.accountId
      ? buildBaasAccountDetailPath(wallet.accountId)
      : BAAS_STABLES_WALLET_PATH;

  return (
    <button
      type="button"
      className="flex w-full min-w-0 items-center justify-between gap-3 rounded-lg px-2 py-3 text-left transition hover:bg-gray-50/80 dark:hover:bg-brand-dark-elevated/40"
      onClick={() => onNavigate(to)}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100">
          {wallet.alias || wallet.asset || wallet.id}
        </p>
        <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">{wallet.id}</p>
        {wallet.accountId ? (
          <p className="mt-0.5 truncate text-xs text-gray-400 dark:text-gray-500">
            {intl.get("baas.entidades.detail.walletAccount", { accountId: wallet.accountId })}
          </p>
        ) : null}
      </div>
      <span className="shrink-0 text-xs font-semibold text-[#a66f1d] dark:text-brand-gold-mid">
        {intl.get("baas.entidades.detail.openWallet")} →
      </span>
    </button>
  );
};

const RelatedListModalBody = ({ activeCard, activeItems, onClose, onNavigate }) => {
  const Icon = activeCard.icon;

  return (
    <div className="px-5 py-6 sm:px-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e7a73f]/15">
            <Icon className="h-5 w-5 text-[#a66f1d]" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-brand-dark dark:text-gray-100">
              {intl.get(activeCard.listTitleKey)}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.detail.modalHint")}
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label={intl.get("words.close")}
          onClick={onClose}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-brand-dark-elevated dark:hover:text-gray-100"
        >
          <TbX className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {activeItems.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{intl.get(activeCard.emptyKey)}</p>
      ) : (
        <ul className="max-h-[min(60vh,28rem)] divide-y divide-gray-100 overflow-y-auto dark:divide-brand-dark-border">
          {activeCard.key === "stablesWallets"
            ? activeItems.map((wallet) => (
                <li key={wallet.id}>
                  <WalletRow wallet={wallet} onNavigate={onNavigate} />
                </li>
              ))
            : activeItems.map((account) => (
                <li key={account.id}>
                  <AccountRow account={account} onNavigate={onNavigate} />
                </li>
              ))}
        </ul>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        {activeItems.length > 0 ? (
          <Link
            to={activeCard.viewAllPath}
            onClick={onClose}
            className="text-xs font-semibold text-[#a66f1d] dark:text-brand-gold-mid"
          >
            {intl.get(activeCard.viewAllKey)}
          </Link>
        ) : (
          <span />
        )}
        <button type="button" className="extract-filter-panel__btn" onClick={onClose}>
          {intl.get("words.close")}
        </button>
      </div>
    </div>
  );
};

/**
 * Entity detail summary: compact count cards → modal list → dedicated detail route.
 * Cards-only (emissor) hides Contas / Contas virtuais / Stables — those are correntista rails.
 */
export const BaasEntidadeRelatedAccountsPanel = ({ instance, entity }) => {
  const history = useHistory();
  const { capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const visibleCards = useMemo(
    () =>
      capabilitiesLoading
        ? []
        : SUMMARY_CARDS.filter((card) => card.isVisible(capabilities)),
    [capabilities, capabilitiesLoading],
  );
  const [accounts, setAccounts] = useState([]);
  const [virtualWallets, setVirtualWallets] = useState([]);
  const [stablesWallets, setStablesWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCardKey, setActiveCardKey] = useState(null);

  const needsAccounts = visibleCards.some(
    (card) => card.key === "accounts" || card.key === "stablesWallets",
  );
  const needsVirtuals = visibleCards.some((card) => card.key === "virtualWallets");
  const needsStables = visibleCards.some((card) => card.key === "stablesWallets");

  useEffect(() => {
    let cancelled = false;

    if (!instance || !entity?.id || visibleCards.length === 0) {
      setAccounts([]);
      setVirtualWallets([]);
      setStablesWallets([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    setLoading(true);
    setError("");

    (async () => {
      try {
        const [nextAccounts, virtualPayload] = await Promise.all([
          needsAccounts
            ? fetchBaasAccountsForEntity(instance, entity).catch(() => [])
            : Promise.resolve([]),
          needsVirtuals
            ? fetchBaasVirtualAccounts(instance, { entityId: entity.id }).catch(() => ({
                items: [],
              }))
            : Promise.resolve({ items: [] }),
        ]);
        if (cancelled) return;

        const nextVirtuals = Array.isArray(virtualPayload?.items) ? virtualPayload.items : [];
        const nextStables = needsStables
          ? await fetchBaasStablesWalletsForAccounts(instance, nextAccounts).catch(() => [])
          : [];
        if (cancelled) return;

        setAccounts(nextAccounts);
        setVirtualWallets(nextVirtuals);
        setStablesWallets(nextStables);
      } catch {
        if (!cancelled) {
          setAccounts([]);
          setVirtualWallets([]);
          setStablesWallets([]);
          setError(intl.get("baas.entidades.detail.relatedLoadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    instance,
    entity?.id,
    entity?.name,
    entity?.type,
    visibleCards.length,
    needsAccounts,
    needsVirtuals,
    needsStables,
  ]);

  const listsByKey = useMemo(
    () => ({
      accounts,
      virtualWallets,
      stablesWallets,
    }),
    [accounts, virtualWallets, stablesWallets],
  );

  const counts = {
    accounts: accounts.length,
    virtualWallets: virtualWallets.length,
    stablesWallets: stablesWallets.length,
  };

  const activeCard = visibleCards.find((card) => card.key === activeCardKey) || null;
  const activeItems = activeCard ? listsByKey[activeCard.key] || [] : [];

  const closeModal = () => setActiveCardKey(null);

  const navigateAndClose = (to) => {
    closeModal();
    history.push(to);
  };

  if (capabilitiesLoading) {
    return null;
  }

  if (visibleCards.length === 0) {
    return null;
  }

  return (
    <div className="app-card p-4 sm:p-5">
      <SectionHeading>{intl.get("baas.entidades.detail.relatedTitle")}</SectionHeading>
      <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
        {intl.get("baas.entidades.detail.relatedSubtitle")}
      </p>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.relatedLoading")}
        </p>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : (
        <div
          className={`grid w-full grid-cols-1 gap-2.5 ${
            visibleCards.length >= 3
              ? "sm:grid-cols-3"
              : visibleCards.length === 2
                ? "sm:grid-cols-2"
                : ""
          }`}
        >
          {visibleCards.map(({ key, icon: Icon, labelKey }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveCardKey(key)}
              className="flex w-full min-w-0 flex-col gap-1.5 rounded-xl border border-gray-200 px-3.5 py-2.5 text-left transition hover:-translate-y-0.5 hover:border-[#e7a73f] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/50 dark:border-brand-dark-border dark:hover:border-brand-gold-mid"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-5 shrink-0 items-center justify-center">
                  <Icon className="h-4 w-4 text-[#e7a73f]" aria-hidden />
                </span>
                <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
                  {intl.get(labelKey)}
                </p>
              </div>
              <p className="text-xl font-semibold tabular-nums leading-none text-brand-dark dark:text-gray-100">
                {counts[key]}
              </p>
            </button>
          ))}
        </div>
      )}

      <Dialog
        open={Boolean(activeCard)}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "!rounded-2xl !shadow-card" }}
      >
        {activeCard ? (
          <RelatedListModalBody
            activeCard={activeCard}
            activeItems={activeItems}
            onClose={closeModal}
            onNavigate={navigateAndClose}
          />
        ) : null}
      </Dialog>
    </div>
  );
};

export default BaasEntidadeRelatedAccountsPanel;
